/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, Modal, Form, Select, DatePicker, InputNumber,
         Tag, Spin, message, Tooltip } from 'antd';
import { PlusOutlined, CarOutlined, UserOutlined, DownloadOutlined,
         WarningOutlined, SearchOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
         CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from 'recharts';
import FleetService from '../../services/FleetService';
import TripMapPicker from './TripMapPicker';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Option } = Select;

// ─── SHARED: Period Filter Component ────────────────────────────────────────
// Drop this into any file that needs time period filtering
// Usage: <PeriodFilter value={period} onChange={setPeriod} />
// period = { preset: 'this_month'|'last_3m'|'this_year'|'last_year'|'custom'|'all', from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
// Filter data: filterByPeriod(data, period, dateField)

const { RangePicker } = DatePicker;

const PRESETS = [
    { key: 'this_month', label: 'This Month',   from: () => moment().startOf('month'),   to: () => moment() },
    { key: 'last_3m',    label: 'Last 3 Months',from: () => moment().subtract(3,'months'),to:() => moment() },
    { key: 'this_year',  label: 'This Year',     from: () => moment().startOf('year'),    to: () => moment() },
    { key: 'last_year',  label: 'Last Year',     from: () => moment().subtract(1,'year').startOf('year'), to: () => moment().subtract(1,'year').endOf('year') },
    { key: 'all',        label: 'All Time',      from: () => null, to: () => null },
];

const DEFAULT_PERIOD = { preset: 'this_year', from: moment().startOf('year').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') };

function PeriodFilter({ value, onChange, style }) {
    const cur = value || DEFAULT_PERIOD;
    const handlePreset = (preset) => {
        const p = PRESETS.find(x => x.key === preset);
        onChange({
            preset,
            from: p.from() ? p.from().format('YYYY-MM-DD') : '',
            to:   p.to()   ? p.to().format('YYYY-MM-DD')   : '',
        });
    };
    const handleRange = ([s, e]) => {
        onChange({
            preset: 'custom',
            from: s ? s.format('YYYY-MM-DD') : '',
            to:   e ? e.format('YYYY-MM-DD') : '',
        });
    };
    return (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', ...style }}>
            <span style={{ fontSize:11, color:'#888', whiteSpace:'nowrap' }}>Period:</span>
            {PRESETS.map(p => (
                <button key={p.key} onClick={() => handlePreset(p.key)} style={{
                    padding:'3px 10px', border:'1px solid', borderRadius:12, cursor:'pointer',
                    fontSize:11, fontWeight: cur.preset===p.key ? 600 : 400,
                    background: cur.preset===p.key ? '#1a3a5c' : '#fff',
                    borderColor: cur.preset===p.key ? '#1a3a5c' : '#d9d9d9',
                    color: cur.preset===p.key ? '#fff' : '#595959',
                }}>{p.label}</button>
            ))}
            <RangePicker size="small" style={{ width:210 }}
                value={cur.preset==='custom' && cur.from && cur.to
                    ? [moment(cur.from), moment(cur.to)] : null}
                onChange={handleRange}
                format="DD MMM YYYY"
                placeholder={['Custom from','Custom to']}
            />
            {cur.from && cur.to && (
                <span style={{ fontSize:10, color:'#1a5b9a', background:'#e6f4ff',
                    padding:'2px 8px', borderRadius:8, whiteSpace:'nowrap' }}>
                    {moment(cur.from).format('DD MMM YYYY')} → {moment(cur.to).format('DD MMM YYYY')}
                </span>
            )}
        </div>
    );
}

function filterByPeriod(data, period, dateField) {
    if (!period || period.preset === 'all' || (!period.from && !period.to)) return data;
    return data.filter(r => {
        const d = r[dateField];
        if (!d || d === '0000-00-00') return false;
        const m = moment(d);
        if (period.from && m.isBefore(moment(period.from), 'day')) return false;
        if (period.to   && m.isAfter(moment(period.to),   'day')) return false;
        return true;
    });
}

const fmtD = (v) => (!v || v === '0000-00-00') ? '—' : moment(v).format('DD MMM YYYY');
const toN  = (v) => Number(v || 0);

// ─── Colour palette ───────────────────────────────────────────────────────────
const PIE_C = ['#1890ff','#52c41a','#faad14','#f5222d','#722ed1','#13c2c2'];

// ─── Mini KPI card ────────────────────────────────────────────────────────────
function KPI({ label, value, color, sub }) {
    return (
        <div style={{ background: '#fff', border: '1px solid #e8e8e8',
            borderRadius: 8, padding: '12px 16px', borderTop: `3px solid ${color}` }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{sub}</div>}
        </div>
    );
}

// ─── Anomaly detection helpers ────────────────────────────────────────────────
function detectAnomalies(logs) {
    const issues = [];
    logs.forEach(r => {
        const km = toN(r.odometer_end) - toN(r.odometer_start);
        const litres = toN(r.fuel_litres);
        // 1. Odometer end < start
        if (r.odometer_end && r.odometer_start && toN(r.odometer_end) < toN(r.odometer_start)) {
            issues.push({ id: r.id, type: 'error', msg: `${r.registration_no} — Odometer end (${r.odometer_end}) < start (${r.odometer_start}) on ${fmtD(r.trip_date)}` });
        }
        // 2. Unusually high consumption > 30L/100km
        if (km > 0 && litres > 0 && (litres / km * 100) > 30) {
            issues.push({ id: r.id, type: 'warn', msg: `${r.registration_no} — High fuel use: ${(litres/km*100).toFixed(1)}L/100km on ${fmtD(r.trip_date)}` });
        }
        // 3. Unusually large single trip > 800km
        if (km > 800) {
            issues.push({ id: r.id, type: 'warn', msg: `${r.registration_no} — Very long trip: ${km} km on ${fmtD(r.trip_date)}` });
        }
        // 4. Fuel refuel > 120L (likely data entry error)
        if (litres > 120) {
            issues.push({ id: r.id, type: 'error', msg: `${r.registration_no} — Fuel entry ${litres}L seems too high on ${fmtD(r.trip_date)}` });
        }
        // 5. Odometer manually adjusted vs map estimate
        if (r.odometer_source === 'manual_adjusted' && toN(r.odometer_discrepancy_pct) > 20) {
            const pct = toN(r.odometer_discrepancy_pct);
            const diff = toN(r.odometer_discrepancy_km);
            const severity = pct > 50 ? 'error' : 'warn';
            issues.push({ id: r.id, type: severity,
                msg: `${r.registration_no} — Odometer ${diff > 0 ? '+' : ''}${diff}km (${pct.toFixed(0)}%) off map estimate on ${fmtD(r.trip_date)} · map: ${r.map_distance_km}km, recorded: ${km}km` });
        }
    });
    return issues;
}

export default function FleetTripLogs() {
    const [logs,          setLogs]          = useState([]);
    const [vehicles,      setVehicles]       = useState([]);
    const [loading,       setLoading]        = useState(true);
    const [modalOpen,     setModalOpen]      = useState(false);
    const [mapRoute,      setMapRoute]       = useState(null);
    const [selVehicle,    setSelVehicle]     = useState(null);  // full vehicle object for odometer auto-fill
    const [saving,        setSaving]         = useState(false);
    const [filterVehicle, setFilterVehicle]  = useState('');
    const [filterPurpose, setFilterPurpose]  = useState('');
    const [period,        setPeriod]         = useState(DEFAULT_PERIOD);
    const [search,        setSearch]         = useState('');
    const [showAnomalies, setShowAnomalies]  = useState(false);
    const [form] = Form.useForm();

    useEffect(() => { loadVehicles(); loadLogs(); }, []);

    const loadVehicles = async () => {
        try {
            // Use vehiclesList (the proper endpoint) not reportStatus
            const r = await FleetService.getVehiclesList();
            if (Number(r?.data?.code) === 200) setVehicles(r.data.result || []);
        } catch (e) {}
    };

    const loadLogs = async (vehicleId = '') => {
        setLoading(true);
        try {
            const r = vehicleId
                ? await FleetService.getTripLogs(vehicleId)
                : await FleetService.getAllTripLogs();
            if (Number(r?.data?.code) === 200) {
                setLogs(r.data.result?.data || r.data.result || []);
            }
        } catch (e) { message.error('Could not load trip logs'); }
        setLoading(false);
    };

    const saveTripLog = async (vals) => {
        setSaving(true);
        try {
            vals.trip_date = vals.trip_date?.format('YYYY-MM-DD');
            // Convert multi-select purpose array → comma string for DB
            if (Array.isArray(vals.purpose)) vals.purpose = vals.purpose.join(', ');
            // ── Odometer discrepancy detection ─────────────────────────────────
            // If both map distance and manual odometer are filled, compare them
            const mapDist   = mapRoute?.distanceKm || mapRoute?.totalDistanceKm;
            const manualKm  = vals.odometer_end && vals.odometer_start
                ? vals.odometer_end - vals.odometer_start : null;
            if (mapDist && manualKm !== null) {
                const deviation = Math.abs(manualKm - mapDist);
                const pct       = mapDist > 0 ? (deviation / mapDist) * 100 : 0;
                // Flag if manual reading deviates >20% from map estimate
                if (pct > 20) {
                    vals.odometer_discrepancy_km  = +(manualKm - mapDist).toFixed(1);
                    vals.odometer_discrepancy_pct = +pct.toFixed(1);
                    vals.odometer_source          = 'manual_adjusted';
                } else {
                    vals.odometer_source = 'map_confirmed';
                }
            } else if (mapDist && !manualKm) {
                vals.odometer_source = 'map_only';
            } else if (!mapDist && manualKm) {
                vals.odometer_source = 'manual_only';
            }

            // Inject map route data if available
            if (mapRoute) {
                vals.from_location   = mapRoute.fromLocation;
                vals.from_lat        = mapRoute.fromLat;
                vals.from_lng        = mapRoute.fromLng;
                vals.to_location     = mapRoute.toLocation;
                vals.to_lat          = mapRoute.toLat;
                vals.to_lng          = mapRoute.toLng;
                vals.map_distance_km = mapRoute.distanceKm || mapRoute.totalDistanceKm;
                vals.route_polyline  = mapRoute.polyline;
                // Multi-stop: save stops + legs as JSON
                if (mapRoute.stops && mapRoute.stops.length > 2) {
                    vals.route_stops = JSON.stringify(mapRoute.stops);
                    vals.route_legs  = JSON.stringify(mapRoute.legs);
                }
                // If odometer end empty, auto-suggest from map distance
                const dist = mapRoute.distanceKm || mapRoute.totalDistanceKm;
                if (dist && !vals.odometer_end && vals.odometer_start) {
                    vals.odometer_end = Math.round(vals.odometer_start + dist);
                }
            }
            const r = await FleetService.saveTripLog(vals);
            if (Number(r?.data?.code) === 200) {
                message.success(r.data.message || 'Trip logged');
                setModalOpen(false);
                form.resetFields();
                loadLogs(filterVehicle);
            } else {
                message.error(r.data.message || 'Failed to save');
            }
        } catch (e) { message.error('Save failed'); }
        setSaving(false);
    };

    // ── Stats ──────────────────────────────────────────────────────────────────
    const totalKm   = logs.reduce((s, r) => s + (toN(r.odometer_end) - toN(r.odometer_start)), 0);
    const totalFuel = logs.reduce((s, r) => s + toN(r.fuel_litres), 0);
    const totalCost = logs.reduce((s, r) => s + toN(r.fuel_cost), 0);
    const avgConsumption = useMemo(() => {
        const valid = logs.filter(r => toN(r.fuel_litres) > 0 && (toN(r.odometer_end) - toN(r.odometer_start)) > 0);
        if (!valid.length) return null;
        const km = valid.reduce((s, r) => s + toN(r.odometer_end) - toN(r.odometer_start), 0);
        const lt = valid.reduce((s, r) => s + toN(r.fuel_litres), 0);
        return (lt / km * 100).toFixed(1);
    }, [logs]);

    // ── Chart data ────────────────────────────────────────────────────────────
    const periodLogs = useMemo(() => filterByPeriod(logs, period, 'trip_date'), [logs, period]);

    const purposeData = useMemo(() => {
        const m = {};
        periodLogs.forEach(r => {
            // purpose may be comma-separated for multi-purpose trips
            const purposes = r.purpose ? r.purpose.split(',').map(p => p.trim()) : ['Unknown'];
            purposes.forEach(p => { m[p] = (m[p] || 0) + 1; });
        });
        return Object.entries(m).map(([name, value]) => ({ name, value })).sort((a,b) => b.value - a.value);
    }, [periodLogs]);

    const monthlyKmData = useMemo(() => {
        const m = {};
        periodLogs.forEach(r => {
            const mo = moment(r.trip_date).format('MMM YY');
            m[mo] = (m[mo] || 0) + (toN(r.odometer_end) - toN(r.odometer_start));
        });
        return Object.entries(m).slice(-8).map(([month, km]) => ({ month, km }));
    }, [periodLogs]);

    const topVehiclesData = useMemo(() => {
        const m = {};
        periodLogs.forEach(r => {
            const key = r.registration_no || r.vehicle_name || `Vehicle ${r.vehicle_id}`;
            m[key] = (m[key] || 0) + (toN(r.odometer_end) - toN(r.odometer_start));
        });
        return Object.entries(m).sort((a,b) => b[1]-a[1]).slice(0,6)
            .map(([name, km]) => ({ name, km }));
    }, [periodLogs]);

    // ── Anomalies ─────────────────────────────────────────────────────────────
    const anomalies = useMemo(() => detectAnomalies(periodLogs), [periodLogs]);

    // ── Months for filter ─────────────────────────────────────────────────────
    const months = useMemo(() => {
        const s = new Set(logs.map(r => moment(r.trip_date).format('YYYY-MM')));
        return [...s].sort().reverse();
    }, [logs]);

    // ── Filter ────────────────────────────────────────────────────────────────
    const filtered = logs.filter(r => {
        const q = search.toLowerCase();
        const ms = !q
            || (r.registration_no || '').toLowerCase().includes(q)
            || (r.driver_name     || '').toLowerCase().includes(q)
            || (r.purpose         || '').toLowerCase().includes(q)
            || (r.destination     || '').toLowerCase().includes(q);
        const mv = !filterVehicle || String(r.vehicle_id) === String(filterVehicle);
        const mp = !filterPurpose || (r.purpose && r.purpose.split(',').map(p=>p.trim()).includes(filterPurpose));
        const mm = filterByPeriod([r], period, 'trip_date').length > 0;
        return ms && mv && mp && mm;
    });

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(filtered.map(r => ({
            Date: r.trip_date, Vehicle: r.registration_no || r.serial_no,
            Driver: r.driver_name, Purpose: r.purpose, Destination: r.destination,
            'Odo Start': r.odometer_start, 'Odo End': r.odometer_end,
            'KM': toN(r.odometer_end) - toN(r.odometer_start),
            'Fuel (L)': r.fuel_litres, 'Fuel Cost (E)': r.fuel_cost,
            'L/100km': r.fuel_litres && (toN(r.odometer_end)-toN(r.odometer_start))>0
                ? (toN(r.fuel_litres)/(toN(r.odometer_end)-toN(r.odometer_start))*100).toFixed(1) : '',
            Condition: r.condition_on_departure, Remarks: r.remarks,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Trip Logs');
        saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]),
            `TripLogs_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    const PURPOSES = ['Patient Transfer','Outreach','Supply Delivery',
        'Staff Transport','Maintenance','Official Business','Other'];

    return (
        <div className="page-content">
            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700 }}>Trip Logs</h4>
                    <div style={{ fontSize:13, color:'#888', marginTop:2 }}>
                        Record and analyse all vehicle journeys
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<DownloadOutlined />} onClick={exportXlsx} disabled={!logs.length}>
                        Export
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
                        Log Trip
                    </Button>
                </div>
            </div>

            {/* ── KPI Cards ── */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:12, marginBottom:20 }}>
                <KPI label="Total Trips"     value={logs.length}                        color="#1a5b9a" />
                <KPI label="Total Distance"  value={`${totalKm.toLocaleString()} km`}   color="#d46b08" />
                <KPI label="Total Fuel"      value={`${totalFuel.toFixed(0)} L`}         color="#389e0d" />
                <KPI label="Total Fuel Cost" value={`E ${totalCost.toLocaleString()}`}  color="#722ed1" />
                <KPI label="Avg Consumption" value={avgConsumption ? `${avgConsumption} L/100km` : '—'} color="#1890ff"
                    sub="fleet average" />
                <KPI label="Anomalies"
                    value={anomalies.length}
                    color={anomalies.length > 0 ? '#f5222d' : '#52c41a'}
                    sub={anomalies.length > 0 ? 'click to review' : 'all clear'} />
            </div>

            {/* ── Anomaly Alert Banner ── */}
            {anomalies.length > 0 && (
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:8,
                    padding:'12px 16px', marginBottom:16, cursor:'pointer' }}
                    onClick={() => setShowAnomalies(p => !p)}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontWeight:700, color:'#cf1322', fontSize:13 }}>
                            <WarningOutlined style={{ marginRight:8 }} />
                            {anomalies.length} irregularit{anomalies.length > 1 ? 'ies' : 'y'} detected — click to {showAnomalies ? 'hide' : 'review'}
                        </span>
                        <ArrowUpOutlined style={{ color:'#cf1322', transform: showAnomalies ? 'rotate(0deg)' : 'rotate(180deg)', transition:'transform .2s' }} />
                    </div>
                    {showAnomalies && (
                        <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:5 }}>
                            {anomalies.map((a, i) => (
                                <div key={i} style={{ fontSize:12, display:'flex', gap:8, alignItems:'center' }}>
                                    <Tag color={a.type === 'error' ? 'red' : 'orange'} style={{ fontSize:10 }}>
                                        {a.type === 'error' ? 'Error' : 'Warning'}
                                    </Tag>
                                    {a.msg}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Period filter for charts ── */}
            {logs.length > 0 && (
                <div style={{ marginBottom:10 }}>
                    <PeriodFilter value={period} onChange={setPeriod} />
                </div>
            )}

            {/* ── Mini Charts Dashboard ── */}
            {logs.length > 0 && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1.6fr 1.4fr', gap:12, marginBottom:20 }}>
                    {/* Trips by Purpose */}
                    <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8, padding:'12px 14px' }}>
                        <div style={{ fontWeight:600, fontSize:12, marginBottom:8 }}>Trips by Purpose</div>
                        <ResponsiveContainer width="100%" height={160}>
                            <PieChart>
                                <Pie data={purposeData} dataKey="value" nameKey="name"
                                    cx="50%" cy="50%" outerRadius={65} labelLine={false}>
                                    {purposeData.map((_, i) => <Cell key={i} fill={PIE_C[i % PIE_C.length]} />)}
                                </Pie>
                                <RTooltip formatter={(v, n) => [v, n]} />
                                <Legend iconSize={9} wrapperStyle={{ fontSize:10 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* KM per month */}
                    <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8, padding:'12px 14px' }}>
                        <div style={{ fontWeight:600, fontSize:12, marginBottom:8 }}>Distance per Month (km)</div>
                        <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={monthlyKmData} margin={{ top:0, right:10, left:0, bottom:0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" tick={{ fontSize:10 }} />
                                <YAxis tick={{ fontSize:10 }} />
                                <RTooltip formatter={(v) => [`${v.toLocaleString()} km`, 'Distance']} />
                                <Bar dataKey="km" fill="#1890ff" radius={[3,3,0,0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Top vehicles by km */}
                    <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8, padding:'12px 14px' }}>
                        <div style={{ fontWeight:600, fontSize:12, marginBottom:8 }}>Top Vehicles by Distance</div>
                        <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={topVehiclesData} layout="vertical"
                                margin={{ top:0, right:40, left:65, bottom:0 }}>
                                <XAxis type="number" tick={{ fontSize:10 }} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={60} />
                                <RTooltip formatter={(v) => [`${v.toLocaleString()} km`, 'KM']} />
                                <Bar dataKey="km" fill="#d46b08" radius={[0,3,3,0]}>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* ── Filters ── */}
            <div style={{ display:'flex', gap:10, marginBottom:14, background:'#fafafa',
                border:'1px solid #e8e8e8', borderRadius:6, padding:'10px 14px', flexWrap:'wrap' }}>
                <Input prefix={<SearchOutlined style={{ color:'#ccc' }} />}
                    placeholder="Search vehicle, driver, destination…"
                    value={search} onChange={e => setSearch(e.target.value)}
                    allowClear style={{ width:260 }} />
                <Select allowClear showSearch optionFilterProp="children"
                    placeholder="Filter by vehicle" style={{ width:220 }}
                    value={filterVehicle || undefined}
                    onChange={v => { setFilterVehicle(v || ''); loadLogs(v || ''); }}>
                    {vehicles.map(v => (
                        <Option key={v.id} value={String(v.id)}>
                            {v.registration_no} — {v.vehicle_type}
                        </Option>
                    ))}
                </Select>
                <Select allowClear placeholder="Filter by purpose" style={{ width:180 }}
                    value={filterPurpose || undefined} onChange={v => setFilterPurpose(v || '')}>
                    {PURPOSES.map(p => <Option key={p} value={p}>{p}</Option>)}
                </Select>
<PeriodFilter value={period} onChange={setPeriod} />
                <div style={{ marginLeft:'auto', fontSize:12, color:'#888', alignSelf:'center' }}>
                    {filtered.length} of {logs.length} trips
                </div>
            </div>

            {/* ── Table ── */}
            <Spin spinning={loading}>
                {filtered.length === 0 && !loading ? (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa',
                        border:'2px dashed #e8e8e8', borderRadius:8 }}>
                        <CarOutlined style={{ fontSize:40, marginBottom:12, display:'block' }} />
                        {logs.length === 0 ? 'No trips logged yet. Click "Log Trip" to add one.' : 'No records match your filters.'}
                    </div>
                ) : (
                    <div style={{ border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                            <thead>
                                <tr style={{ background:'#1a3a5c', color:'#fff' }}>
                                    {['Date','Vehicle','Driver','Purpose','Destination',
                                      'Distance','Fuel (L)','L/100km','Cost (E)','Condition','Remarks'].map(h => (
                                        <th key={h} style={{ padding:'10px 12px', textAlign:'left',
                                            fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r, idx) => {
                                    const km      = toN(r.odometer_end) - toN(r.odometer_start);
                                    const lper100 = (r.fuel_litres && km > 0)
                                        ? (toN(r.fuel_litres) / km * 100).toFixed(1) : null;
                                    const isAnomaly = anomalies.some(a => a.id === r.id);
                                    return (
                                        <tr key={r.id || idx} style={{
                                            background: isAnomaly ? '#fff9f9'
                                                : idx%2===0 ? '#fff' : '#f9f9f9',
                                            borderBottom:'1px solid #f0f0f0',
                                            borderLeft: isAnomaly ? '3px solid #f5222d' : '3px solid transparent',
                                        }}>
                                            <td style={{ padding:'9px 12px', whiteSpace:'nowrap' }}>
                                                {fmtD(r.trip_date)}
                                                {isAnomaly && (
                                                    <Tooltip title={anomalies.filter(a => a.id === r.id).map(a => a.msg).join(' | ')}>
                                                        <WarningOutlined style={{ color:'#f5222d', marginLeft:5 }} />
                                                    </Tooltip>
                                                )}
                                            </td>
                                            <td style={{ padding:'9px 12px' }}>
                                                <div style={{ fontWeight:700, color:'#1a5b9a' }}>
                                                    <CarOutlined style={{ marginRight:4 }} />
                                                    {r.registration_no || r.vehicle_name || '—'}
                                                </div>
                                            </td>
                                            <td style={{ padding:'9px 12px' }}>
                                                <UserOutlined style={{ color:'#52c41a', marginRight:4 }} />
                                                {r.driver_name || '—'}
                                            </td>
                                            <td style={{ padding:'9px 12px' }}>
                                                <span style={{ display:'flex', flexWrap:'wrap', gap:2 }}>
                                                {r.purpose
                                                    ? r.purpose.split(',').map((p,i) =>
                                                        <Tag key={i} style={{ fontSize:9, margin:0 }}>{p.trim()}</Tag>)
                                                    : '—'}
                                                </span>
                                            </td>
                                            <td style={{ padding:'9px 12px', maxWidth:130 }}>
                                                <span style={{ overflow:'hidden', textOverflow:'ellipsis',
                                                    whiteSpace:'nowrap', display:'block' }}>
                                                    {r.destination || '—'}
                                                </span>
                                            </td>
                                            <td style={{ padding:'9px 12px', textAlign:'right' }}>
                                                {km > 0 ? (
                                                    <span>
                                                        <Tag color="blue">{km.toLocaleString()} km</Tag>
                                                        {r.odometer_source === 'manual_adjusted' && toN(r.odometer_discrepancy_pct) > 20 && (
                                                            <Tooltip title={`Map estimated ${r.map_distance_km}km · manual ${toN(r.odometer_discrepancy_pct).toFixed(0)}% deviation`}>
                                                                <Tag color={toN(r.odometer_discrepancy_pct) > 50 ? 'red' : 'orange'}
                                                                    style={{ fontSize:9, marginLeft:2 }}>
                                                                    {toN(r.odometer_discrepancy_km) > 0 ? '+' : ''}{r.odometer_discrepancy_km}km
                                                                </Tag>
                                                            </Tooltip>
                                                        )}
                                                        {r.odometer_source === 'map_confirmed' && (
                                                            <Tooltip title="Matches map estimate">
                                                                <Tag color="green" style={{ fontSize:9, marginLeft:2 }}>✓</Tag>
                                                            </Tooltip>
                                                        )}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td style={{ padding:'9px 12px', textAlign:'right' }}>
                                                {r.fuel_litres ? Number(r.fuel_litres).toFixed(1) : '—'}
                                            </td>
                                            <td style={{ padding:'9px 12px', textAlign:'right',
                                                fontWeight: lper100 && Number(lper100) > 20 ? 700 : 400,
                                                color: lper100 && Number(lper100) > 20 ? '#f5222d'
                                                    : lper100 && Number(lper100) > 15 ? '#d46b08' : '#333' }}>
                                                {lper100 ? `${lper100}` : '—'}
                                            </td>
                                            <td style={{ padding:'9px 12px', textAlign:'right', color:'#389e0d' }}>
                                                {r.fuel_cost ? `E ${Number(r.fuel_cost).toLocaleString()}` : '—'}
                                            </td>
                                            <td style={{ padding:'9px 12px' }}>
                                                <Tag color={
                                                    r.condition_on_departure === 'Good' ? 'green'
                                                    : r.condition_on_departure === 'Minor issues' ? 'orange' : 'red'
                                                }>{r.condition_on_departure}</Tag>
                                            </td>
                                            <td style={{ padding:'9px 12px', fontSize:11, color:'#888',
                                                maxWidth:120 }}>
                                                <span style={{ overflow:'hidden', textOverflow:'ellipsis',
                                                    whiteSpace:'nowrap', display:'block' }}>
                                                    {r.remarks || '—'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {filtered.length > 0 && (
                                <tfoot>
                                    <tr style={{ background:'#f0f5ff', borderTop:'2px solid #adc6ff' }}>
                                        <td colSpan={5} style={{ padding:'8px 12px', fontWeight:700,
                                            fontSize:12, color:'#2f54eb' }}>
                                            TOTAL ({filtered.length} trips)
                                        </td>
                                        <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:700, color:'#1890ff' }}>
                                            {filtered.reduce((s,r) => s + toN(r.odometer_end)-toN(r.odometer_start), 0).toLocaleString()} km
                                        </td>
                                        <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:700 }}>
                                            {filtered.reduce((s,r) => s + toN(r.fuel_litres), 0).toFixed(1)} L
                                        </td>
                                        <td />
                                        <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:700, color:'#389e0d' }}>
                                            E {filtered.reduce((s,r) => s + toN(r.fuel_cost), 0).toLocaleString()}
                                        </td>
                                        <td colSpan={2} />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                )}
            </Spin>

            {/* ── Log Trip Modal ── */}
            <Modal title="Log a Trip" open={modalOpen}
                onCancel={() => { setModalOpen(false); form.resetFields(); setMapRoute(null); setSelVehicle(null); }}
                footer={null} width={560} destroyOnClose>
                <Form form={form} layout="vertical" onFinish={saveTripLog}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
                        <Form.Item name="vehicle_id" label="Vehicle" rules={[{ required:true }]}
                            style={{ gridColumn:'1/-1' }}>
                            <Select showSearch optionFilterProp="children" placeholder="Select vehicle"
                                onSelect={vid => {
                                    const v = vehicles.find(x => x.id === vid || String(x.id) === String(vid));
                                    setSelVehicle(v || null);
                                    if (v?.current_odometer) {
                                        form.setFieldsValue({ odometer_start: Number(v.current_odometer) });
                                    }
                                }}>
                                {vehicles.map(v => (
                                    <Option key={v.id} value={v.id}>
                                        {v.registration_no} — {v.vehicle_type}
                                        {v.current_odometer ? ` | odo: ${Number(v.current_odometer).toLocaleString()} km` : ''}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="trip_date" label="Trip Date"
                            initialValue={moment()} rules={[{ required:true }]}>
                            <DatePicker style={{ width:'100%' }} format="DD MMM YYYY" />
                        </Form.Item>
                        <Form.Item name="purpose" label="Purpose" rules={[{ required:true, type:'array' }]}>
                            <Select mode="multiple" placeholder="Select one or more purposes"
                                maxTagCount={2} maxTagTextLength={12}>
                                {['Patient Transfer','Outreach','Supply Delivery','Staff Transport',
                                  'Maintenance','Official Business','Other'].map(p => (
                                    <Option key={p} value={p}>{p}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {/* Purpose + Destination in a side-by-side row */}
                        <Form.Item name="destination" label="Destination" style={{ gridColumn:'1/-1' }}>
                            <Input placeholder="Auto-filled from map, or type manually" />
                        </Form.Item>
                        {/* Map picker — full width */}
                        <Form.Item label="Route map" style={{ gridColumn:'1/-1', marginBottom:4 }}>
                            <TripMapPicker
                                onRouteChange={route => {
                                    setMapRoute(route);
                                    if (route) {
                                        form.setFieldsValue({ destination: route.toLocation });
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item name="odometer_start"
                            label={<span>Odometer Start (km){selVehicle?.current_odometer
                                ? <span style={{ fontSize:11, color:'#52c41a', marginLeft:6, fontWeight:400 }}>
                                    · auto-filled from last trip
                                  </span>
                                : null}</span>}>
                            <InputNumber style={{ width:'100%' }} min={0}
                                onChange={() => {/* manual override allowed */}}
                                placeholder={selVehicle?.current_odometer
                                    ? `Last known: ${Number(selVehicle.current_odometer).toLocaleString()} km`
                                    : 'Enter start reading'} />
                        </Form.Item>
                        <Form.Item name="odometer_end"
                            label={<span>Odometer End (km){(mapRoute?.distanceKm || mapRoute?.totalDistanceKm)
                                ? <span style={{ fontSize:11, color:'#1a3a5c', marginLeft:6, fontWeight:400 }}>
                                    · map suggests +{mapRoute.distanceKm || mapRoute.totalDistanceKm} km
                                  </span>
                                : null}</span>}>
                            <InputNumber style={{ width:'100%' }} min={0} />
                        </Form.Item>
                        <Form.Item name="fuel_litres" label="Fuel Added (litres)">
                            <InputNumber style={{ width:'100%' }} min={0} precision={1} />
                        </Form.Item>
                        <Form.Item name="fuel_cost" label="Fuel Cost (E)">
                            <InputNumber style={{ width:'100%' }} min={0} precision={2} />
                        </Form.Item>
                        <Form.Item name="condition_on_departure" label="Vehicle Condition"
                            initialValue="Good" style={{ gridColumn:'1/-1' }}>
                            <Select>
                                <Option value="Good">Good</Option>
                                <Option value="Minor issues">Minor issues</Option>
                                <Option value="Requires attention">Requires attention</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="remarks" label="Remarks" style={{ gridColumn:'1/-1' }}>
                            <Input.TextArea rows={2} placeholder="Any issues, incidents or notes" />
                        </Form.Item>
                    </div>
                    <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                        <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={saving}>Save Trip Log</Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}